<!--
  NarrativeCard.vue — AI-generated natural language summary card.

  Props:
    agent       — 'centinela' | 'jefe' | 'supervisor' | 'ingeniero'
    title       — Card headline
    narrative   — The AI-generated text body
    timestamp   — ISO string or relative time label
    severity    — optional: 'critical' | 'high' | 'medium' | 'low' | 'info' | 'ok'
    loading     — boolean: show skeleton while AI is generating
    actions     — array of { label, icon, handler }
    glass       — boolean: compact dark C5-panel row instead of VCard
    unread      — boolean: unread left-border accent (glass mode only)
-->
<template>
  <!-- ═══ Glass / C5 mode ═══ -->
  <div
    v-if="glass"
    class="c5-event-row"
    :class="{ 'c5-event-row--unread': unread }"
    @click="$emit('click', $event)"
  >
    <div
      class="c5-event-icon"
      :style="{ background: `rgba(var(--v-theme-${agentColor}), 0.15)`, color: `rgb(var(--v-theme-${agentColor}))` }"
    >
      <VIcon size="13" :icon="agentIcon" />
    </div>

    <div class="c5-event-info">
      <div class="c5-event-title">{{ title }}</div>
      <div class="c5-event-narrative">{{ narrative }}</div>
    </div>

    <div class="c5-event-side">
      <span class="c5-event-time">{{ timestamp }}</span>
      <span
        v-if="severity && severity !== 'info'"
        class="c5-sev-pip"
        :class="`c5-sev-pip--${severity}`"
      />
    </div>
  </div>

  <!-- ═══ Normal / Dashboard mode ═══ -->
  <VCard
    v-else
    :class="[
      'narrative-card',
      `agent-accent--${agent}`,
    ]"
    rounded="lg"
    :loading="loading"
  >
    <!-- Header -->
    <VCardItem>
      <template #prepend>
        <VAvatar
          :color="agentColor"
          size="36"
          variant="tonal"
        >
          <VIcon size="20">{{ agentIcon }}</VIcon>
        </VAvatar>
      </template>

      <VCardTitle class="narrative-headline">
        {{ title }}
      </VCardTitle>

      <VCardSubtitle class="d-flex align-center gap-2">
        <span class="narrative-caption">{{ agentLabel }}</span>
        <span v-if="timestamp" class="narrative-caption">· {{ timestamp }}</span>
        <StatusBadge
          v-if="severity"
          :severity="severity"
          size="small"
          class="ml-2"
        />
      </VCardSubtitle>
    </VCardItem>

    <!-- Narrative Body -->
    <VCardText>
      <div v-if="loading" class="d-flex flex-column gap-2">
        <VSkeletonLoader type="text" />
        <VSkeletonLoader type="text" width="80%" />
        <VSkeletonLoader type="text" width="60%" />
      </div>
      <p v-else class="narrative-text mb-0">
        {{ narrative }}
      </p>
    </VCardText>

    <!-- Optional slot for charts / quick data below the narrative -->
    <slot name="detail" />

    <!-- Action Buttons -->
    <VCardActions v-if="actions && actions.length > 0">
      <VSpacer />
      <VBtn
        v-for="(action, idx) in actions"
        :key="idx"
        variant="text"
        :color="agentColor"
        size="small"
        @click="action.handler"
      >
        <VIcon v-if="action.icon" start size="16">{{ action.icon }}</VIcon>
        {{ action.label }}
      </VBtn>
    </VCardActions>
  </VCard>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import StatusBadge from './StatusBadge.vue'

const { t } = useI18n()

const props = defineProps({
  agent: {
    type: String,
    required: true,
    validator: v => ['centinela', 'jefe', 'supervisor', 'ingeniero'].includes(v),
  },
  title: { type: String, required: true },
  narrative: { type: String, default: '' },
  timestamp: { type: String, default: '' },
  severity: { type: String, default: null },
  loading: { type: Boolean, default: false },
  actions: { type: Array, default: () => [] },
  /** Compact dark glass row for C5 panel */
  glass: { type: Boolean, default: false },
  /** Unread left-border accent (glass mode) */
  unread: { type: Boolean, default: false },
})

const AGENTS = {
  centinela:  { key: 'dashboard.agents.centinela',  icon: 'mdi-shield-lock-outline',   color: 'agent-centinela' },
  jefe:       { key: 'dashboard.agents.jefe',        icon: 'mdi-truck-outline',         color: 'agent-jefe' },
  supervisor: { key: 'dashboard.agents.supervisor',   icon: 'mdi-clipboard-check-outline', color: 'agent-supervisor' },
  ingeniero:  { key: 'dashboard.agents.ingeniero',    icon: 'mdi-office-building-cog-outline', color: 'agent-ingeniero' },
}

const agentLabel = computed(() => AGENTS[props.agent]?.key ? t(AGENTS[props.agent].key) : props.agent)
const agentIcon  = computed(() => AGENTS[props.agent]?.icon  || 'mdi-robot-outline')
const agentColor = computed(() => AGENTS[props.agent]?.color || 'primary')
</script>

<style lang="scss" scoped>
/* ─── Normal card ─── */
.narrative-card {
  transition: box-shadow 0.25s ease, transform 0.2s ease;
  &:hover { transform: translateY(-1px); }
}

/* ─── Glass / C5 row ─── */
.c5-event-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
  width: 100%;

  &:hover { background: rgba(255, 255, 255, 0.04); }
}

.c5-event-row--unread {
  border-inline-start: 2px solid rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.04);
}

.c5-event-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 1px;
}

.c5-event-info {
  flex: 1;
  min-width: 0;
}

.c5-event-title {
  font-size: 12px;
  font-weight: 500;
  color: #e2e8f0;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.c5-event-narrative {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.c5-event-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.c5-event-time {
  font-size: 9px;
  color: rgba(255, 255, 255, 0.3);
  white-space: nowrap;
  font-family: 'JetBrains Mono', monospace;
}

.c5-sev-pip {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;

  &--critical { background: #ef4444; }
  &--high     { background: #f59e0b; }
  &--medium   { background: #06b6d4; }
  &--low      { background: #94a3b8; }
}
</style>
